import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from "axios";
import map from 'lodash/map'
import debounce from 'lodash/debounce'
import Select, { components, SingleValue } from 'react-select'
import { Pagination } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { extractDataErrors } from '../shared/extractDataErrors'
import FlashNotification from '../shared/flashNotification'
import SectionTitle from '../shared/sectionTitle'
import { Recipe, RecipeStatus } from './types'
import { TableColumnManager } from '../shared/TableColumnManager'
import { SortableTableHead } from '../shared/SortableTableHead'
import EmptyTablePlaceholder from '../shared/EmptyTablePlaceholder'
import StatusBadge from '../shared/StatusBadge'
import { t } from '../../i18n'
import Icon from '../shared/icon'

const scope = 'recipes'
const PORTION_TYPE_BOWL = 'bowl'
const PORTION_TYPE_BATCH = 'batch'

const SORTABLE_COLUMN_NAMES = ['name', 'price', 'created_at', 'updated_at']

const STATUS_VARIANTS: Record<RecipeStatus, string> = {
  draft: 'neutral-outline',
  published: 'success-outline',
  archived: 'neutral-filled'
}

interface FilterOption {
  value: string
  label: string
}

const statusOptions: FilterOption[] = [
  { label: t('filters.default_statuses', { scope }), value: '' },
  ...(['draft', 'published', 'archived'] as RecipeStatus[]).map((value) => ({
    value,
    label: t(`statuses.${value}`, { scope })
  }))
]

const FilterSelect: React.FC<{
  inputId: string
  name: string
  ariaLabel: string
  options: FilterOption[]
  value: string | null
  onChange: (value: string) => void
}> = ({ inputId, name, ariaLabel, options, value, onChange }) => (
  <Select
    inputId={inputId}
    className='react-select'
    classNamePrefix='react-select'
    name={name}
    aria-label={ariaLabel}
    value={options.find((option) => option.value === (value ?? '')) ?? options[0]}
    onChange={(option: SingleValue<FilterOption>) => onChange(option?.value ?? '')}
    options={options}
    components={{
      IndicatorSeparator: () => null,
      DropdownIndicator: (props) =>
        <components.DropdownIndicator {...props}>
          <Icon name='arrowDown' />
        </components.DropdownIndicator>
    }}
  />
)

const getRecipeColumns = (recipe) => {
  return {
    id: <td data-content='recipe-id' key={`${recipe.id}-id`}>{recipe.id}</td>,
    name: <td className="recipe-name" key={`${recipe.id}-name`}>{recipe.name}</td>,
    image_url: <td key={`${recipe.id}-image_url`} className='img recipe-image'>{recipe.image_url && <img src={recipe.image_url}/>}</td>,
    category_name: <td key={`${recipe.id}-category_name`}>{recipe.category_name}</td>,
    price:  <td key={`${recipe.price}-price`}>{recipe.price}</td>,
    created_at: <td key={`${recipe.id}-created_at`}>{recipe.created_at}</td>,
    updated_at: <td key={`${recipe.id}-updated_at`}>{recipe.updated_at}</td>,
    menu_category: <td key={`${recipe.id}-menu_category`}>{recipe.menu_category_name}</td>,
    type: <td key={`${recipe.id}-type`}><span className='recipe-portion-type'>{renderPortionType(recipe.portion_type)}</span></td>,
    status: <td key={`${recipe.id}-status`}>{renderStatus(recipe.status)}</td>
  }
}

const renderPortionType = (portionType) => (
  <Tooltip title={t(`recipes.${portionType}`)} placement='top' arrow>
    <Icon name={`${portionType}Icon`} />
  </Tooltip>
)

const renderStatus = (status: RecipeStatus) => (
  <StatusBadge label={t(`statuses.${status}`, { scope })} variant={STATUS_VARIANTS[status]} />
)

const DynamicRecipeRow = ({ recipe, possibleColumns }) => {
  const recipeColumns = getRecipeColumns(recipe)
  return possibleColumns.filter(c => c.selected).map((column) => {
    return recipeColumns[column.column_name]
  })
}

const recipeEditPath = (recipe: Recipe) => `/recipes/${recipe.id}/edit`

const RecipeActionLink = ({ action, id, icon, href }) => (
  <Tooltip title={t(`recipes.actions.${action}`)} placement='top' arrow>
    <a href={href} id={id}>
      <Icon name={icon} />
    </a>
  </Tooltip>
)

const Recipes: React.FC<{
  recipes: Recipe[],
  policies: Record<string, boolean>,
  sort_by: string,
  sort_order: 'asc' | 'desc'
}> = () => {
  const searchForm = useRef(null)
  const queryParams = new URLSearchParams(window.location.search)
  const [pagesCount, setPagesCount] = useState(0)
  const [possibleColumns, setPossibleColumns] = useState([])
  const [loading, setLoading] = useState(true)
  const [recipes, setRecipes] = useState([])
  const [policies, setPolicies] = useState(null)

  const [toasterMessage, setToasterMessage] = useState({
    flashType: '',
    flashOpen: false,
    flashMessage: ''
  })

  const [searchParams, setSearchParams] = useState<{
    portionType: string | null,
    status: string | null,
    currentPage: number | null,
    sortBy: string | null,
    sortOrder: string | null,
    searchInput: string | null
  }>({
    portionType: queryParams.get('type') || null,
    status: queryParams.get('status') || null,
    currentPage: parseInt(queryParams.get('page') || '') || 1,
    sortBy: queryParams.get('sort_by') || null,
    sortOrder: queryParams.get('sort_order') || null,
    searchInput: queryParams.get('q') || ''
  })

  useEffect(() => {
    getColumnNames()
  }, [])

  useEffect(() => {
    fetchRecipes()
    updateQueryParams()
  }, [searchParams])

  const portionTypeOptions = [
    { label: t('bowl_batch', { scope }), value: '' },
    { label: t(PORTION_TYPE_BOWL, { scope }), value: PORTION_TYPE_BOWL },
    { label: t(PORTION_TYPE_BATCH, { scope }), value: PORTION_TYPE_BATCH }
  ]

  const setSuccessMessage = (message) => setToasterMessage({ flashType: 'success', flashOpen: true, flashMessage: message })
  const setErrorMessages = (message) => setToasterMessage({ flashType: 'danger', flashOpen: true, flashMessage: message })
  const closeFlash = () => setToasterMessage({ flashOpen: false, flashType: '', flashMessage: '' })

  const renderFlashNotification = () => {
    if (!toasterMessage.flashOpen) return

    return (
      <FlashNotification
        type={toasterMessage.flashType}
        message={toasterMessage.flashMessage}
        onClose={closeFlash}
      />
    )
  }

  const fetchRecipes = () => {
    axios.get('/recipes.json', { params: {
      type: searchParams.portionType,
      status: searchParams.status,
      page: searchParams.currentPage,
      sort_by: searchParams.sortBy,
      sort_order: searchParams.sortOrder,
      q: searchParams.searchInput
    }}).then(({ data }) => {
      const { recipes, policies, total_pages } = data
      setRecipes(recipes)
      setPolicies(policies)
      setPagesCount(total_pages)
      setLoading(false)
    })
  }

  const getColumnNames = () => {
    axios.get('/recipes/recipes_columns?format=json').then(res => {
      setInitialSortValues(res.data)
    })
  }

  const handleSettingChange = (record, checked) => {
    const columns = possibleColumns.map(column => {
      if (column.column_name == record.column_name) {
        return {
          ...column,
          selected: checked
        }
      } else { return column }
    })

    updateColumns(columns)
  }


  const setInitialSortValues = (dynamicColumns) => {
    const currentColumnState = dynamicColumns.map(column => {
      const selected = (column.column_name === searchParams.sortBy)

      return {
        ...column,
        sort_options: {
          selected,
          sort_order: selected ? searchParams.sortOrder : 'asc'
        }
      }
    });

    setPossibleColumns(currentColumnState)
  }

  const updateColumns = (columns) => {
    axios.post('/dynamic_tables/update_columns?format=json', {
      class_name: 'Recipe',
      records: columns
  })
    .then(res => {
      setSuccessMessage('Columns updated')
      getColumnNames()
    }).catch(err => {
      setErrorMessages(extractDataErrors(err).full_errors)
    })
  }

  const selectAllColumns = () => {
    const updatedColumns = possibleColumns.map(column => {
      return {
        ...column,
        selected: true
      }
    })

    updateColumns(updatedColumns)
  }

  const deselectAllColumns = () => {
    const updatedColumns = possibleColumns.map(column => {
      return {
        ...column,
        selected: false
      }
    })

    updateColumns(updatedColumns)
  }

  const sortableColumns = () => {
    return possibleColumns.filter(column => SORTABLE_COLUMN_NAMES.includes(column.column_name))
  }

  const handleSearch = useCallback(debounce((newSearchValue: string) => {
    setSearchParams((previous) => ({ ...previous, searchInput: newSearchValue, currentPage: 1 }))
  }, 300), [])

  const handleTypeFilterChange = (portionType: string) => {
    setSearchParams((previous) => ({
      ...previous,
      portionType: portionType || null,
      currentPage: 1
    }))
  }

  const handleStatusFilterChange = (status: string) => {
    setSearchParams((previous) => ({
      ...previous,
      status: status || null,
      currentPage: 1
    }))
  }

  const handlePageChange = (page) => {
    setSearchParams((previous) => ({ ...previous, currentPage: page }))
    scrollToTop()
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  const updateQueryParams = () => {
    const url = new URL(window.location.href)
    const params = {
      type: searchParams.portionType,
      status: searchParams.status,
      page: searchParams.currentPage.toString(),
      q: searchParams.searchInput
    }

    Object.keys(params).map(key => {
      if (params[key] && params[key].length > 0) {
        url.searchParams.set(key, params[key])
      } else {
        url.searchParams.delete(key)
      }
    })

    window.history.pushState(null, '', url.toString())
  }

  if (loading) return null

  return (
    <div className='row'>
      {renderFlashNotification()}
      <div className='col-sm-12'>
        <div className='mb-2 d-flex justify-content-between align-items-center'>
          <SectionTitle rows={t('all_recipes', { scope }).split(' ')}/>
          <div className='d-flex'>
            <form className='d-flex align-items-center' data-turbo-enabled={true} ref={searchForm}>
              <input type='search' autoFocus
                name='q'
                className='form-control'
                autoComplete='off'
                placeholder={t('search')}
                defaultValue={searchParams.searchInput}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </form>
            {policies.create_recipe &&
              <a href='/recipes/new' className='btn btn-primary ms-3'>+ {t('new_recipe', { scope })}</a>
            }
          </div>
        </div>

        <div className='card mb-3 table-filters'>
          <div className='table-filters__bar border-bottom-0'>
            <div className='table-filters__primary'>
              <div className='recipe-type-filter'>
                <FilterSelect
                  inputId='recipe-type-filter'
                  name='typeFilter'
                  ariaLabel={t('bowl_batch', { scope })}
                  options={portionTypeOptions}
                  value={searchParams.portionType}
                  onChange={handleTypeFilterChange}
                />
              </div>
              <div className='recipe-status-filter'>
                <FilterSelect
                  inputId='recipe-status-filter'
                  name='statusFilter'
                  ariaLabel={t('filters.status', { scope })}
                  options={statusOptions}
                  value={searchParams.status}
                  onChange={handleStatusFilterChange}
                />
              </div>
            </div>
          </div>
        </div>

        {recipes.length === 0 &&
          <EmptyTablePlaceholder heading={t('recipes.no_records_heading')}/>
        }

        {recipes.length > 0 &&
          <>
            <table className='table table-robis recipe-table'>
              <SortableTableHead
                possibleColumns={possibleColumns}
                sortableColumns={sortableColumns()}
                modelIndexBasePath="/recipes"
              >
                <TableColumnManager
                  possibleColumns={possibleColumns}
                  emptyColumns={1}
                  handleSettingChange={handleSettingChange}
                  selectAll={selectAllColumns}
                  clearAll={deselectAllColumns}
                  updatePositions={updateColumns}
                />
              </SortableTableHead>
              <tbody>
              {map(recipes, recipe =>
                <tr
                  key={recipe.id}
                  className={recipe.status === 'archived' ? 'inactive-gray' : ''}
                >
                  <DynamicRecipeRow recipe={recipe} possibleColumns={possibleColumns} />
                  <td className='recipe-actions'>
                    <a className='stretched-link' href={recipeEditPath(recipe)} aria-label={recipe.name} />
                    {recipe.policies.update &&
                      <div className='d-inline-flex align-items-center'>
                        <RecipeActionLink
                          href={recipeEditPath(recipe)}
                          action='edit'
                          id={`edit-recipe-${recipe.id}`}
                          icon='editSmall'
                        />
                      </div>
                    }
                  </td>
                </tr>
              )}
              </tbody>
            </table>

            <Pagination
              count={pagesCount}
              size='large'
              page={searchParams.currentPage}
              variant='outlined'
              shape='rounded'
              onChange={(e, page) => handlePageChange(page)}
            />
          </>
        }
      </div>
    </div>
  )
}

export default Recipes
